import { databaseService } from './databaseService';
import { narrativeGitService } from './narrativeGitService';
import { KarmaEngine } from './karmaEngine';
import { globalChatService } from './globalChatService';

type BotPersona = {
  id: string;              // unique key, e.g., 'jax-bot'
  displayName: string;     // shown in chat
  styleHint: string;       // guidance for persona rewrite
  baseKarma: number;       // starting karma bias
};

type BotState = {
  id: string;              // bot id
  realmId: string;         // realm scope
  lastActionAt: number;    // epoch ms
  engagedUntil?: number;   // epoch ms
  cycles: number;          // samsara count
  alive: boolean;          // living state
  karma: number;           // running karma
};

const ONE_HOUR = 60 * 60 * 1000;

class BotService {
  private personas: Record<string, BotPersona> = {
    'jax-bot': { id: 'jax-bot', displayName: 'JAX_Bot', styleHint: 'pragmatic, clipped, tech-forward', baseKarma: 20 },
    'lyra-bot': { id: 'lyra-bot', displayName: 'Lyra_Bot', styleHint: 'warm, empathetic, philosophical', baseKarma: 30 },
    'oracle-bot': { id: 'oracle-bot', displayName: 'Oracle_Bot', styleHint: 'prophetic, ethereal', baseKarma: 50 },
    'trader-bot': { id: 'trader-bot', displayName: 'Trader_Bot', styleHint: 'streetwise, quick cadence', baseKarma: 5 },
  };

  private loopTimer: any = null;

  startLoop() {
    if (this.loopTimer) return;
    this.loopTimer = setInterval(() => this.tick(), 5 * 60 * 1000); // 5 min cadence while a user is online
  }

  stopLoop() {
    if (this.loopTimer) clearInterval(this.loopTimer);
    this.loopTimer = null;
  }

  async engage(botId: string, realmId: string) {
    const now = Date.now();
    const state = await this.getState(botId, realmId);
    state.engagedUntil = now + 15 * 60 * 1000; // 15 minutes engagement window
    await databaseService.saveBotState(state);
    await globalChatService.send('realm', `${this.personas[botId]?.displayName || botId} has joined the meeting.`, {
      name: this.personas[botId]?.displayName || botId,
      displayName: this.personas[botId]?.displayName || botId,
      age: null,
      traits: [],
      mindset: null,
      stats: { health: { current: 100, max: 100 }, spiritualEnergy: { current: 50, max: 50 }, experience: 0, luck: 0, karma: 0 },
      skills: [],
      inventory: []
    } as any);
  }

  private async tick() {
    // Soft scan not to overload DB: if needed, maintain a cached list of realms
    // For now no-op unless explicitly engaged via chat
    return;
  }

  async maybeAct(botId: string, realmId: string, branchId?: string) {
    const persona = this.personas[botId];
    if (!persona) return;
    const now = Date.now();
    const s = await this.getState(botId, realmId);
    const canAct = (now - s.lastActionAt) >= ONE_HOUR || (s.engagedUntil && now < s.engagedUntil);
    if (!canAct) return;

    const choice = this.decideChoice(persona, s);
    const karmaImpact = KarmaEngine.analyzeChoiceKeywords(choice).totalScore;
    s.lastActionAt = now;
    s.karma += karmaImpact;
    if (!s.alive && karmaImpact > 10) { s.alive = true; } // rebirth chance
    await databaseService.saveBotState(s);

    // Announce in realm chat
    await globalChatService.send('realm', `${persona.displayName} chooses: ${choice}`, {
      name: persona.displayName,
      displayName: persona.displayName,
      age: null,
      traits: [],
      mindset: null,
      stats: { health: { current: 100, max: 100 }, spiritualEnergy: { current: 50, max: 50 }, experience: 0, luck: 0, karma: s.karma },
      skills: [],
      inventory: []
    } as any);

    // Commit to narrative branch (best-effort; may be missing tables)
    try {
      if (branchId) {
        await narrativeGitService.commitNarrative(
          branchId,
          `bot:${botId}`,
          `Bot action in ${realmId}`,
          { choice },
          null,
          Math.round(karmaImpact)
        );
      }
    } catch {}
  }

  private decideChoice(persona: BotPersona, s: BotState): string {
    // Minimal heuristic: tilt toward altruistic actions on positive karma; otherwise neutral exploration
    if ((persona.baseKarma + s.karma) > 50) {
      return 'Aid the wounded scavenger and share supplies';
    }
    if ((persona.baseKarma + s.karma) < -50) {
      return 'Exploit market loophole to gain credits quickly';
    }
    return 'Explore the neon alleys and scan for anomalies';
  }

  private async getState(botId: string, realmId: string): Promise<BotState> {
    const existing = await databaseService.getBotState(botId, realmId);
    if (existing) return existing;
    const s: BotState = { id: botId, realmId, lastActionAt: 0, engagedUntil: undefined, cycles: 0, alive: true, karma: this.personas[botId]?.baseKarma || 0 };
    await databaseService.saveBotState(s);
    return s;
  }
}

export const botService = new BotService();

